package br.com.vvrefeicoes.portal.data.repository

import br.com.vvrefeicoes.portal.core.util.formatIsoDateTime
import br.com.vvrefeicoes.portal.domain.model.Guide
import br.com.vvrefeicoes.portal.domain.model.GuideBlock
import br.com.vvrefeicoes.portal.domain.model.GuideBlockType
import br.com.vvrefeicoes.portal.domain.model.GuideContentType
import br.com.vvrefeicoes.portal.domain.model.GuideDraft
import br.com.vvrefeicoes.portal.domain.model.GuideMedia
import br.com.vvrefeicoes.portal.domain.model.GuidePage
import br.com.vvrefeicoes.portal.domain.model.GuideRoleRef
import br.com.vvrefeicoes.portal.domain.model.GuideStatus
import br.com.vvrefeicoes.portal.domain.model.GuideSummary
import br.com.vvrefeicoes.portal.domain.model.GuideVersion
import br.com.vvrefeicoes.portal.domain.repository.AuthRepository
import br.com.vvrefeicoes.portal.domain.repository.GuideRepository
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.HttpRequestBuilder
import io.ktor.client.request.delete
import io.ktor.client.request.forms.MultiPartFormDataContent
import io.ktor.client.request.forms.formData
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.patch
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.Headers
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import io.ktor.http.encodeURLParameter
import io.ktor.http.isSuccess
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

class RemoteGuideRepository(
    private val httpClient: HttpClient,
    private val baseUrl: String,
    private val authRepository: AuthRepository,
) : GuideRepository {
    override suspend fun loadGuidesPage(
        page: Int,
        size: Int,
        query: String,
    ): GuidePage {
        val path =
            buildString {
                append("/guides?page=").append(page).append("&size=").append(size)
                if (query.isNotBlank()) append("&q=").append(query.encodeURLParameter())
            }
        val pageDto = get<GuidePageDto>(path)
        return GuidePage(pageDto.content.map { it.toDomain() }, pageDto.page.totalPages)
    }

    override suspend fun loadGuide(id: String): Guide = get<GuideDto>("/guides/$id").toDomain()

    override suspend fun loadPublishedGuide(id: String): Guide = get<GuideDto>("/guides/$id/published").toDomain()

    override suspend fun loadVersions(id: String): List<GuideVersion> = get<List<GuideVersionDto>>("/guides/$id/versions").map { it.toDomain() }

    override suspend fun create(draft: GuideDraft): Guide = post<GuideDto>("/guides", draft.toDto()).toDomain()

    override suspend fun update(
        id: String,
        draft: GuideDraft,
    ): Guide = put<GuideDto>("/guides/$id", draft.toDto()).toDomain()

    override suspend fun publish(id: String): Guide = postEmpty<GuideDto>("/guides/$id/publish").toDomain()

    override suspend fun setActive(
        id: String,
        active: Boolean,
    ): Guide = patch<GuideDto>("/guides/$id/active", GuideActiveDto(active)).toDomain()

    override suspend fun updateAccess(
        id: String,
        allRoles: Boolean,
        roleIds: List<String>,
    ): Guide = put<GuideDto>("/guides/$id/access", GuideAccessDto(allRoles, roleIds)).toDomain()

    override suspend fun delete(id: String) {
        request { httpClient.delete(baseUrl + "/guides/$id") { authorize() } }
    }

    override suspend fun loadRoles(): List<GuideRoleRef> = get<List<GuideRoleDto>>("/guides/roles").map { GuideRoleRef(it.id, it.name) }

    override suspend fun uploadMedia(
        bytes: ByteArray,
        contentType: String,
        fileName: String,
        kind: GuideBlockType,
    ): GuideMedia {
        val response =
            httpClient.post("$baseUrl/guides/media") {
                authorize()
                setBody(
                    MultiPartFormDataContent(
                        formData {
                            append("kind", kind.name)
                            append(
                                "file",
                                bytes,
                                Headers.build {
                                    append(HttpHeaders.ContentType, contentType)
                                    append(HttpHeaders.ContentDisposition, "filename=\"$fileName\"")
                                },
                            )
                        },
                    ),
                )
            }
        if (!response.status.isSuccess()) {
            ensureSuccess(response.status.value, false, response.bodyAsText())
        }
        return response.body<GuideMediaDto>().toDomain()
    }

    override suspend fun loadMedia(id: String): ByteArray {
        val response = request { httpClient.get("$baseUrl/guides/media/$id") { authorize() } }
        return response.body()
    }

    override suspend fun loadMediaUrl(id: String): String = get<GuideMediaUrlDto>("/guides/media/$id/url").url

    override suspend fun uploadImage(
        bytes: ByteArray,
        contentType: String,
    ): String {
        val extension = if (contentType.equals("image/png", ignoreCase = true)) "png" else "jpg"
        val response =
            httpClient.post("$baseUrl/guides/images") {
                authorize()
                setBody(
                    MultiPartFormDataContent(
                        formData {
                            append(
                                "file",
                                bytes,
                                Headers.build {
                                    append(HttpHeaders.ContentType, contentType)
                                    append(HttpHeaders.ContentDisposition, "filename=\"guide-image.$extension\"")
                                },
                            )
                        },
                    ),
                )
            }
        if (!response.status.isSuccess()) {
            ensureSuccess(response.status.value, false, response.bodyAsText())
        }
        return response.body<GuideImageDto>().url
    }

    override suspend fun loadImage(path: String): ByteArray {
        val normalized = if (path.startsWith("/")) path else "/$path"
        val response = request { httpClient.get(baseUrl + normalized) { authorize() } }
        return response.body()
    }

    private suspend inline fun <reified T> get(path: String): T = request { httpClient.get(baseUrl + path) { authorize() } }.body()

    private suspend inline fun <reified T> post(
        path: String,
        body: Any,
    ): T = request { httpClient.post(baseUrl + path) { jsonBody(body) } }.body()

    private suspend inline fun <reified T> postEmpty(path: String): T = request { httpClient.post(baseUrl + path) { authorize() } }.body()

    private suspend inline fun <reified T> put(
        path: String,
        body: Any,
    ): T = request { httpClient.put(baseUrl + path) { jsonBody(body) } }.body()

    private suspend inline fun <reified T> patch(
        path: String,
        body: Any,
    ): T = request { httpClient.patch(baseUrl + path) { jsonBody(body) } }.body()

    private suspend inline fun request(crossinline call: suspend () -> HttpResponse): HttpResponse {
        val response = call()
        if (!response.status.isSuccess()) {
            ensureSuccess(response.status.value, false, response.bodyAsText())
        }
        return response
    }

    private fun ensureSuccess(
        status: Int,
        success: Boolean,
        body: String,
    ) {
        if (success) return
        val message =
            runCatching {
                Json { ignoreUnknownKeys = true }
                    .decodeFromString<GuideApiErrorDto>(body)
                    .message
            }.getOrNull()
        error(message ?: "HTTP $status")
    }

    private fun HttpRequestBuilder.authorize() {
        authRepository.session.value?.token?.let { token ->
            header(HttpHeaders.Authorization, "Bearer $token")
        }
    }

    private fun HttpRequestBuilder.jsonBody(body: Any) {
        authorize()
        contentType(ContentType.Application.Json)
        setBody(body)
    }
}

@Serializable
private data class GuideApiErrorDto(
    val message: String? = null,
)

@Serializable
private data class GuidePageDto(
    val content: List<GuideSummaryDto> = emptyList(),
    val page: GuidePageMetadataDto = GuidePageMetadataDto(),
)

@Serializable
private data class GuidePageMetadataDto(
    val totalPages: Int = 1,
)

@Serializable
private data class GuideSummaryDto(
    val id: String = "",
    val title: String = "",
    val excerpt: String = "",
    val status: String = "RASCUNHO",
    val contentType: String = "TEXT",
    val active: Boolean = true,
    val version: Int? = null,
    val createdByName: String? = null,
    val updatedAt: String? = null,
    val publishedAt: String? = null,
)

@Serializable
private data class GuideDto(
    val id: String = "",
    val title: String = "",
    val description: String = "",
    val bodyHtml: String = "",
    val status: String = "RASCUNHO",
    val contentType: String = "TEXT",
    val blocks: List<GuideBlockDto> = emptyList(),
    val active: Boolean = true,
    val version: Int? = null,
    val hasPublishedVersion: Boolean = false,
    val hasDraft: Boolean = false,
    val allRoles: Boolean = true,
    val roleIds: List<String> = emptyList(),
    val createdByName: String? = null,
    val createdAt: String? = null,
    val updatedByName: String? = null,
    val updatedAt: String? = null,
    val publishedAt: String? = null,
    val publishedByName: String? = null,
)

@Serializable
private data class GuideBlockDto(
    val id: String,
    val type: String,
    val html: String? = null,
    val documentId: String? = null,
    val caption: String? = null,
    val fileName: String? = null,
)

@Serializable
private data class GuideVersionDto(
    val id: String = "",
    val version: Int = 0,
    val title: String = "",
    val publishedAt: String? = null,
    val publishedByName: String? = null,
)

@Serializable
private data class GuideRoleDto(
    val id: String = "",
    val name: String = "",
)

@Serializable
private data class GuideActiveDto(
    val active: Boolean,
)

@Serializable
private data class GuideAccessDto(
    val allRoles: Boolean,
    val roleIds: List<String>,
)

@Serializable
private data class GuideImageDto(
    val id: String = "",
    val url: String = "",
)

@Serializable
private data class GuideMediaDto(
    val id: String = "",
    val url: String = "",
    val fileName: String = "",
    val contentType: String = "",
    val sizeBytes: Long = 0,
)

@Serializable
private data class GuideMediaUrlDto(
    val url: String = "",
)

@Serializable
private data class GuideDraftDto(
    val title: String,
    val description: String,
    val blocks: List<GuideBlockDto>,
    val allRoles: Boolean,
    val roleIds: List<String>,
)

private fun GuideDraft.toDto(): GuideDraftDto =
    GuideDraftDto(
        title = title,
        description = description,
        blocks =
            blocks.map { block ->
                GuideBlockDto(
                    id = block.id,
                    type = block.type.name,
                    html = block.html,
                    documentId = block.documentId.takeIf { it.isNotBlank() },
                    caption = block.caption.takeIf { it.isNotBlank() },
                    fileName = block.fileName.takeIf { it.isNotBlank() },
                )
            },
        allRoles = allRoles,
        roleIds = roleIds,
    )

private fun GuideSummaryDto.toDomain(): GuideSummary =
    GuideSummary(
        id = id,
        title = title,
        excerpt = excerpt,
        status = status.toGuideStatus(),
        contentType = contentType.toGuideContentType(),
        active = active,
        version = version,
        createdByName = createdByName,
        updatedAt = updatedAt?.takeIf { it.isNotBlank() }?.let(::formatIsoDateTime),
        publishedAt = publishedAt?.takeIf { it.isNotBlank() }?.let(::formatIsoDateTime),
    )

private fun GuideDto.toDomain(): Guide {
    val resolvedBlocks =
        blocks
            .map { it.toDomain() }
            .ifEmpty {
                if (bodyHtml.isNotBlank()) {
                    listOf(GuideBlock(id = "legacy-$id", type = GuideBlockType.TEXT, html = bodyHtml))
                } else {
                    emptyList()
                }
            }
    return Guide(
        id = id,
        title = title,
        description = description,
        blocks = resolvedBlocks,
        bodyHtml = bodyHtml,
        status = status.toGuideStatus(),
        contentType = contentType.toGuideContentType(),
        active = active,
        version = version,
        hasPublishedVersion = hasPublishedVersion,
        hasDraft = hasDraft,
        allRoles = allRoles,
        roleIds = roleIds,
        createdByName = createdByName,
        createdAt = createdAt?.takeIf { it.isNotBlank() }?.let(::formatIsoDateTime),
        updatedByName = updatedByName,
        updatedAt = updatedAt?.takeIf { it.isNotBlank() }?.let(::formatIsoDateTime),
        publishedAt = publishedAt?.takeIf { it.isNotBlank() }?.let(::formatIsoDateTime),
        publishedByName = publishedByName,
    )
}

private fun GuideBlockDto.toDomain(): GuideBlock =
    GuideBlock(
        id = id,
        type = type.toGuideBlockType(),
        html = html.orEmpty(),
        documentId = documentId.orEmpty(),
        caption = caption.orEmpty(),
        fileName = fileName.orEmpty(),
    )

private fun GuideMediaDto.toDomain(): GuideMedia =
    GuideMedia(
        id = id,
        url = url,
        fileName = fileName,
        contentType = contentType,
        sizeBytes = sizeBytes,
    )

private fun GuideVersionDto.toDomain(): GuideVersion =
    GuideVersion(
        id = id,
        version = version,
        title = title,
        publishedAt = publishedAt?.takeIf { it.isNotBlank() }?.let(::formatIsoDateTime),
        publishedByName = publishedByName,
    )

private fun String.toGuideStatus(): GuideStatus = if (equals("PUBLICADO", ignoreCase = true)) GuideStatus.PUBLICADO else GuideStatus.RASCUNHO

private fun String.toGuideContentType(): GuideContentType =
    when (uppercase()) {
        GuideContentType.PDF.name -> GuideContentType.PDF
        GuideContentType.VIDEO.name -> GuideContentType.VIDEO
        else -> GuideContentType.TEXT
    }

private fun String.toGuideBlockType(): GuideBlockType =
    when (uppercase()) {
        GuideBlockType.IMAGE.name -> GuideBlockType.IMAGE
        GuideBlockType.VIDEO.name -> GuideBlockType.VIDEO
        GuideBlockType.PDF.name -> GuideBlockType.PDF
        else -> GuideBlockType.TEXT
    }
